/**
 * Ride highlights: the moments worth a clip (owner feedback 27). Pure logic over the sim telemetry, no DOM: the
 * session feeds every physics step (and its lap / loop events) in, highlights come out. They drive the "Save a clip?"
 * toast (ClipOfferGate: at most once a minute, only at a calm moment) and the director, which centres the "best 15 s"
 * on the biggest one (src/game/cinematic.ts pickWindow).
 */

/** Seconds of riding a video needs: the "Create video" action appears after this much recorded input. */
export const VIDEO_MIN_RIDE = 15;
export const canFilmRide = (rideSeconds: number): boolean => rideSeconds >= VIDEO_MIN_RIDE;

export type HighlightKind = 'bigJump' | 'personalBest' | 'cleanLap' | 'stylishLanding' | 'wheelie' | 'figure8';

export interface Highlight {
  kind: HighlightKind;
  /** Ride time (s) the moment is centred on: a jump's apex, a lap's finish, a wheelie's middle. */
  t: number;
  /** How much it is worth showing (the director films the biggest). */
  score: number;
  /** s in the air (jumps, landings), m above the takeoff (jumps), s on the back wheel (wheelies), s for the lap. */
  air?: number;
  height?: number;
  seconds?: number;
}

/** What the detector reads from one physics step (a subset of BikeTelemetry plus the bike's height). */
export interface StepSample {
  t: number;
  y: number;
  airborne: boolean;
  fallen: boolean;
  wheelie: boolean;
  /** Set on the step of a judged touchdown (BikeTelemetry.landing). */
  landing: { verdict: 'clean' | 'hard' | 'crash'; noseError: number; airTime: number } | null;
}

export const BIG_JUMP_AIR = 0.8; // s
export const BIG_JUMP_HEIGHT = 2; // m
export const STYLISH_NOSE = 0.2; // rad (11 deg) of nose error: well inside a clean landing (landClean 0.26)
export const STYLISH_MIN_AIR = 0.5; // s
export const LONG_WHEELIE = 2; // s
const WHEELIE_GAP = 0.15; // s the front may tap down without ending the wheelie

/** Scores: a personal best tops everything, then big air (longer and higher is better), then the rest. */
export function scoreOf(h: Omit<Highlight, 'score'>): number {
  switch (h.kind) {
    case 'personalBest': return 40;
    case 'bigJump': return 12 + (h.air ?? 0) * 10 + (h.height ?? 0) * 3;
    case 'cleanLap': return 14;
    case 'figure8': return 12;
    case 'stylishLanding': return 8 + (h.air ?? 0) * 5;
    case 'wheelie': return 5 + (h.seconds ?? 0) * 2;
  }
}

const make = (h: Omit<Highlight, 'score'>): Highlight => ({ ...h, score: scoreOf(h) });

/** Steps the telemetry; returns the highlights each step completes (most steps: none). */
export class HighlightDetector {
  private flying = false;
  private takeoffY = 0;
  private apexY = 0;
  private apexT = 0;
  private wheelieFrom = -1;
  private wheelieLast = -1;
  private fellSinceLap = false;
  private fellSinceLoop = false;

  step(s: StepSample): Highlight[] {
    const out: Highlight[] = [];
    if (s.fallen) { this.fellSinceLap = true; this.fellSinceLoop = true; }
    // Flights: takeoff height and apex; judged on the touchdown.
    if (s.airborne && !s.fallen) {
      if (!this.flying) { this.flying = true; this.takeoffY = s.y; this.apexY = s.y; this.apexT = s.t; }
      if (s.y > this.apexY) { this.apexY = s.y; this.apexT = s.t; }
    }
    if (s.landing) {
      const l = s.landing;
      const height = this.flying ? this.apexY - Math.min(this.takeoffY, s.y) : 0;
      const at = this.flying ? this.apexT : s.t - l.airTime / 2;
      if (l.verdict !== 'crash' && !s.fallen) {
        if (l.airTime > BIG_JUMP_AIR || height > BIG_JUMP_HEIGHT) out.push(make({ kind: 'bigJump', t: at, air: l.airTime, height }));
        else if (l.verdict === 'clean' && Math.abs(l.noseError) < STYLISH_NOSE && l.airTime >= STYLISH_MIN_AIR) out.push(make({ kind: 'stylishLanding', t: at, air: l.airTime }));
      }
      this.flying = false;
    } else if (!s.airborne) this.flying = false;
    // Wheelies on the ground: one highlight when a long one ends.
    const up = s.wheelie && !s.airborne && !s.fallen;
    if (up) {
      if (this.wheelieFrom < 0) this.wheelieFrom = s.t;
      this.wheelieLast = s.t;
    } else if (this.wheelieFrom >= 0 && (s.t - this.wheelieLast > WHEELIE_GAP || s.fallen)) {
      const seconds = this.wheelieLast - this.wheelieFrom;
      if (seconds >= LONG_WHEELIE && !s.fallen) out.push(make({ kind: 'wheelie', t: (this.wheelieFrom + this.wheelieLast) / 2, seconds }));
      this.wheelieFrom = -1;
    }
    return out;
  }

  /** A lap finished at ride time t: a personal best beats a clean lap (no fall in it). */
  lap(t: number, lapTime: number, personalBest: boolean): Highlight | null {
    const clean = !this.fellSinceLap;
    this.fellSinceLap = false;
    if (personalBest) return make({ kind: 'personalBest', t, seconds: lapTime });
    return clean ? make({ kind: 'cleanLap', t, seconds: lapTime }) : null;
  }

  /** A figure-eight loop closed at ride time t (the figure-eight drill): clean when the bike stayed up. */
  loop(t: number): Highlight | null {
    const clean = !this.fellSinceLoop;
    this.fellSinceLoop = false;
    return clean ? make({ kind: 'figure8', t }) : null;
  }
}

/** The biggest highlight (ties: the latest), or null. */
export function topHighlight(list: readonly Highlight[]): Highlight | null {
  let best: Highlight | null = null;
  for (const h of list) if (!best || h.score >= best.score) best = h;
  return best;
}

/** The longest flight among the highlights (s), for the share caption. */
export const longestAir = (list: readonly Highlight[]): number => list.reduce((m, h) => Math.max(m, h.air ?? 0), 0);

export const OFFER_COOLDOWN = 60; // s between two clip toasts
export const OFFER_CALM = 0.8; // s on the ground and upright before a toast may show
export const OFFER_STALE = 8; // s a highlight waits for a calm moment before it is dropped

/**
 * When the "Save a clip?" toast may show: a highlight waits for a calm moment (both wheels down and upright for
 * OFFER_CALM, never in the air or mid-crash), at most one toast per OFFER_COOLDOWN, never before the ride is long
 * enough to film, and never when the player turned it off. A better highlight replaces a waiting one.
 */
export class ClipOfferGate {
  private pending: Highlight | null = null;
  private pendingSince = 0;
  private calmFor = 0;
  private lastShown = -Infinity;

  offer(h: Highlight, now: number): void {
    if (this.pending && this.pending.score > h.score && now - this.pendingSince < OFFER_STALE) return;
    this.pending = h;
    this.pendingSince = now;
  }

  /** One step: `calm` = on the ground, upright. Returns the highlight to show now, or null. */
  step(now: number, dt: number, calm: boolean, rideSeconds: number, enabled: boolean): Highlight | null {
    this.calmFor = calm ? this.calmFor + dt : 0;
    const p = this.pending;
    if (!p) return null;
    // Off, stale, or inside the cooldown: let it go (no queue of old moments).
    if (!enabled || now - this.pendingSince > OFFER_STALE || now - this.lastShown < OFFER_COOLDOWN) { this.pending = null; return null; }
    if (this.calmFor < OFFER_CALM || !canFilmRide(rideSeconds)) return null;
    this.pending = null;
    this.lastShown = now;
    return p;
  }
}
